import { Component, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { startOfToday } from 'date-fns';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { EditorModule } from 'primeng/editor';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { map, Subject, takeUntil } from 'rxjs';

import { AuthState, selectActiveAccount } from '../../../../core/auth/auth.reducer';
import { SelectItem } from '../../../../shared/models/select-item.model';
import { ServiceRequestConstants } from '../../service-request-constants';
import { FileInformation, ServiceRequest } from '../../service-request.model';
import { ServiceRequestService } from '../../service-request.service';

@Component({
  selector: 'carehub-service-request-update',
  imports: [
    BreadcrumbModule,
    DialogModule,
    ButtonModule,
    CardModule,
    InputTextModule,
    EditorModule,
    FormsModule,
    ConfirmDialogModule,
    ToggleSwitchModule,
    SelectModule,
    DatePickerModule,
    TooltipModule,
    ReactiveFormsModule
],
  providers: [ConfirmationService],
  templateUrl: './service-request-update.component.html',
  styleUrl: './service-request-update.component.scss',
})
export class ServiceRequestUpdateComponent implements OnInit, OnDestroy {
  breadcrumbHome: MenuItem = {
    routerLink: '/',
  };

  breadcrumbItems: MenuItem[] = [];

  serviceRequest: WritableSignal<ServiceRequest | null> = signal(null);
  dialogReason: string = '';
  urgencyOptions: SelectItem[] = ServiceRequestConstants.urgencyOptions;
  severityOptions: SelectItem[] = ServiceRequestConstants.severityOptions;
  requestUpdateForm!: FormGroup;
  filterString: string = '';
  today = startOfToday();

  private destroyed$: Subject<void> = new Subject<void>();
  activeAccountEmail: string = '';

  constructor(
    private _confirmationService: ConfirmationService,
    private _serviceRequestService: ServiceRequestService,
    private _store: Store<AuthState>,
    private _formBuilder: FormBuilder,
    private _activatedRoute: ActivatedRoute,
    private _router: Router
  ) {}

  editorModules = {
    toolbar: ['bold', 'italic', 'strike', 'underline', { list: 'ordered' }],
  };

  ngOnInit(): void {
    if (history.state.filter) {
      this.filterString = decodeURIComponent(history.state.filter);
    }

    this.initForm();
    this.setupBreadcrumbs();
    this.loadData();
  }

  private initForm(): void {
    this.requestUpdateForm = this._formBuilder.group({
      title: [{ value: '', disabled: true }, Validators.required],
      description: ['', Validators.required],
      dueDate: [null],
      compliance: [false],
      confidential: [false],
      severity: [{ value: null, disabled: true }, Validators.required],
      urgency: [{ value: null, disabled: true }, Validators.required],
    });
  }

  private setupBreadcrumbs(): void {
    const id: number = this._activatedRoute.snapshot.params['id'];
    this.breadcrumbItems = [
      {
        label: $localize`:@@sr_update.breadcrumb.root:Service Requests`,
        routerLink: '/utilities/service-requests',
      },
      {
        label: id.toString(),
        routerLink: `/utilities/service-requests/${id}`,
      },
    ];

    this._store
      .select(selectActiveAccount)
      .pipe(
        takeUntil(this.destroyed$),
        map(activeAccount => activeAccount?.email)
      )
      .subscribe(email => (this.activeAccountEmail = email ?? ''));
  }

  private loadData(): void {
    const id: number = this._activatedRoute.snapshot.params['id'];

    this._serviceRequestService.show(id).then((request) => {
      const dueDateValue: Date | null = request.dueDate ? new Date(request.dueDate) : null;

      this.requestUpdateForm.patchValue({
        id: request.id,
        title: request.title,
        description: request.description,
        dueDate: dueDateValue,
        compliance: request.compliance || false,
        confidential: request.confidential || false,
        severity: this.findOptionByValue(this.severityOptions, request.severity),
        urgency: this.findOptionByValue(this.urgencyOptions, request.urgency),
      });

      this.serviceRequest.set(request);
    });
  }

  private findOptionByValue(options: SelectItem[], value: unknown): SelectItem | null {
    return options.find(option => option.value === value) || null;
  }

  confirmCloseServiceRequest(): void {
    this._confirmationService.confirm({
      header: 'Confirmation',
      message:
        'To proceed with closing the service request, please confirm and specify the reason.',
      acceptIcon: 'pi pi-check mr-2',
      rejectIcon: 'pi pi-times mr-2',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'danger',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Yes',
      },
      accept: () => {
        this.closeServiceRequest();
      },
    });
  }

  async closeServiceRequest(): Promise<void> {
    const currentRequest: ServiceRequest | null = this.serviceRequest();
    if (!currentRequest) {
      return;
    }

    const updatedRequest: ServiceRequest = {
      ...currentRequest,
      status: 'Closed',
      description: `Service request has been closed by the requester <br>Reason: ${this.dialogReason}<br><br> ---------- <br><br>${currentRequest.description}`,
    };

    const result: ServiceRequest = await this._serviceRequestService.update(updatedRequest);
    this.serviceRequest.set(result);
    this.dialogReason = '';
  }

  update(): void {
    const currentRequest: ServiceRequest | null = this.serviceRequest();
    if (!currentRequest || !this.requestUpdateForm.valid) {
      return;
    }

    const formValue: ServiceRequest = this.requestUpdateForm.value;

    const serviceRequestUpdate: ServiceRequest = {
      ...currentRequest,
      description: formValue.description,
      dueDate: formValue.dueDate,
      compliance: formValue.compliance,
      confidential: formValue.confidential,
      severity: (formValue.severity as SelectItem) ?? (formValue.severity as SelectItem).value,
      urgency: (formValue.urgency as SelectItem) ?? (formValue.urgency as SelectItem).value,
    };

    this._serviceRequestService.update(serviceRequestUpdate);
  }

  goBack(): void {
    if (this.filterString) {
      this._router.navigate(['/utilities/service-requests'], {
        queryParams: { filter: this.filterString },
      });
    } else {
      this._router.navigate(['/utilities/service-requests']);
    }
  }

  checkServiceRequestOwner(requester: string): boolean {
    return this.activeAccountEmail === requester;
  }

  checkServiceRequestAssignedTo(): boolean {
    const currentRequest: ServiceRequest | null = this.serviceRequest();
    return currentRequest ? currentRequest.assignedTo !== 'Unassigned' : false;
  }

  confirmContestServiceRequest(): void {
    this._confirmationService.confirm({
      header: 'Confirmation',
      message: 'Are you sure you want to contest this service requests completion or closure?',
      acceptIcon: 'pi pi-check mr-2',
      rejectIcon: 'pi pi-times mr-2',
      rejectButtonStyleClass: 'p-button',
      acceptButtonStyleClass: 'p-button-outlined p-button',
      accept: () => {
        this.contestServiceRequest();
      },
    });
  }

  async contestServiceRequest(): Promise<void> {
    const currentRequest: ServiceRequest | null = this.serviceRequest();
    const id: number | null = currentRequest?.id ?? null;

    if (id === null) {
      return;
    }

    await this._serviceRequestService.contestServiceRequest(id);
    const updatedRequest: ServiceRequest = await this._serviceRequestService.show(id);
    this.serviceRequest.set(updatedRequest);
    this.dialogReason = '';
  }

  downloadFile(file: FileInformation): void {
    const currentRequest: ServiceRequest | null = this.serviceRequest();
    if (!currentRequest) {
      return;
    }

    this._serviceRequestService
      .downloadFile(currentRequest.id ?? 0, file.guid)
      .then((blob: Blob | void) => {
        if (blob) {
          const url: string = window.URL.createObjectURL(blob);
          const anchor: HTMLAnchorElement = document.createElement('a');
          anchor.href = url;
          anchor.download = file.fileName;
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
        }
      });
  }

  startTeamsChat(): void {
    const currentRequest: ServiceRequest | null = this.serviceRequest();
    if (!currentRequest) {
      return;
    }

    this._serviceRequestService.joinTeamsChat(currentRequest);
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
